import { excludeNullOrUndefined } from 'checkout/utils'
import { sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import moment from 'moment'

import { EXPERIENCE_GIFT_SCHEDULE_DATE_LIMIT_BEFORE_EXPIRATION_DATE } from 'constants/experience'
import getExperienceItemsView from './itemViews/getExperienceItemsView'
import getTransferItemsView from './itemViews/getTransferItemsView'
import getExperienceBreakdownView from './breakdownViews/getExperienceBreakdownView'
import getExperienceItems from './items/getExperienceItems'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'

function getMobileAppDiscountTotal(viewWithStatus: App.WithDataStatus<Array<App.Checkout.ExperienceItemView | App.Checkout.TransferItemView>>) {
  const definedItems = viewWithStatus.data.filter(excludeNullOrUndefined)

  const itemsWithAppDiscount = definedItems.filter((item) => item.totals.mobileAppDiscount?.amount)
  const appDiscountAmount = itemsWithAppDiscount.length ? sum(itemsWithAppDiscount, item => item.totals.mobileAppDiscount?.amount ?? 0) : 0
  const appDiscountPercentage = itemsWithAppDiscount.length ? sum(definedItems, item => item.totals.mobileAppDiscount?.percentage ?? 0) / itemsWithAppDiscount.length : 0

  return {
    amount: appDiscountAmount,
    percentage: appDiscountPercentage,
  }
}

export const getExperienceMobileAppDiscountTotal = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (viewWithStatus): App.Checkout.MobileAppDiscount => getMobileAppDiscountTotal(viewWithStatus),
)

export const getTransferMobileAppDiscountTotal = createSelector(
  (state: App.State) => getTransferItemsView(state),
  (viewWithStatus): App.Checkout.MobileAppDiscount => getMobileAppDiscountTotal(viewWithStatus),
)

export const getExperienceTotalPrice = createSelector(
  (state: App.State) => getExperienceBreakdownView(state),
  (views) => sum(views.data, view => view.price),
)

export const getCheckoutFormFieldCustomProps = createSelector(
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => getExperienceItemsView(state),
  (isGift, experienceItemsView): App.Checkout.FormFieldCustomProps | undefined => {
    // the user can buy an experience gift only in standalone and is always one experience
    const experienceItem = experienceItemsView.data[0]
    const maxScheduleDate = (isGift && experienceItem?.isGift) ? (moment(experienceItem.bookByDate ?? experienceItem.expirationDate).subtract(EXPERIENCE_GIFT_SCHEDULE_DATE_LIMIT_BEFORE_EXPIRATION_DATE, 'days').toDate()) : undefined
    const customProps = {
      gift: {
        maxScheduleDate,
      },
    }

    return customProps
  },
)

export const isSoldOut = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (views): boolean => {
    const isSoldOut = views.data.some(itemView => !itemView.isValid)
    return isSoldOut
  },
)

export const checkIsExperienceGiftOfferOrderMode = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedExperiences(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => getExperienceItems(state),
  (isLuxPlusEnabled, isStandaloneExperience, experienceItems) => {
    const experienceItem = experienceItems[0]
    if (!isLuxPlusEnabled || !isLuxPlusEnabled || !isStandaloneExperience || !experienceItem.isGift) return false
    return experienceItem?.orderMode === 'gift-offer'
  },
)
